import { BestPractices } from '../BestPractices/BestPractices'
import { Box, IconButton, Stack, Typography } from '@dtx-company/design-system/src'
import { Dialog, Skeleton } from '@mui/material'
import { FC, useCallback, useId, useState } from 'react'
import { Flowcode } from '@dtx-company/flowcode-generator-browser'
import { InfoOutlined } from '@mui/icons-material'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { selectDisplayScanDestination } from '@app/code/src/machines/locg/locgSelectors'
import { useFlowcodeOptions } from '../hooks/useFlowcodeOptions'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useLOCGPrismicContext } from '../hooks/useLOCGPrismicContext'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

export const CodePreview: FC = () => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const options = useFlowcodeOptions()
  const [isOpen, setIsOpen] = useState(false)
  const handleClose = useCallback(() => {
    setIsOpen(false)
    fireAnalyticsEvent('LoggedOutGenerator_CodePreview_BestPracticesDialogClosed')
  }, [])
  const displayScanDestination = useSelector(locgService, selectDisplayScanDestination)
  const authSectionActive = useSelector(locgService, state => state.matches('auth'))

  const {
    locgV2Data: {
      code_preview: [captions]
    }
  } = useLOCGPrismicContext()

  const calloutTitleId = useId()
  const calloutDescriptionId = useId()

  return (
    <Stack
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: 'relative',
        flex: {
          md: 0.85,
          lg: 1
        },
        overflowY: {
          md: 'auto'
        },
        py: {
          xs: 6,
          md: 10
        },
        height: {
          xs: '30vh',
          md: '100%'
        },
        backgroundColor: 'surface.2'
      }}
      gap={4}
    >
      <Stack
        alignItems="center"
        component="figure"
        gap={4}
        sx={theme => ({
          m: 0,
          height: '100%',
          width: '100%',
          figcaption: {
            width: { md: '80%', lg: theme.spacing(120) },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 2,
            mb: 4,
            // prevent layout shift when a scan destination is added
            '& > p': {
              minHeight: theme => theme.spacing(4)
            }
          }
        })}
      >
        <Box
          sx={theme => ({
            height: {
              xs: '100%',
              md: 'fit-content'
            },
            // while the skeleton is displayed, set display: contents so the skeleton is centered
            '&:not(:has(img))': {
              display: 'contents'
            },
            img: {
              maxHeight: { xs: theme.spacing(85), md: theme.spacing(51.5), lg: theme.spacing(85) },
              aspectRatio: '1/1',
              mx: 'auto'
            }
          })}
        >
          <Flowcode
            options={options}
            renderLoadingIcon={() => (
              <Skeleton
                sx={theme => ({
                  aspectRatio: '1/1',
                  height: { xs: '100%', md: theme.spacing(51.5), lg: theme.spacing(85) },
                  maxHeight: 'inherit',
                  mx: 'auto'
                })}
                variant="circular"
              />
            )}
          />
        </Box>
        <Typography
          variant="captionMedium"
          align="center"
          component="figcaption"
          data-testid="code-preview-caption"
        >
          {captions?.caption_line_1}
          <br />
          {captions?.caption_line_2}
          <Typography
            variant="labelSmall"
            color="onSurface.primary"
            align="center"
            component="p"
            noWrap
          >
            {displayScanDestination}
          </Typography>
        </Typography>
      </Stack>

      {/* Desktop callout */}
      <Box
        sx={theme => ({
          display: { xs: 'none', md: 'block' },
          width: {
            md: '90%',
            lg: theme.spacing(120)
          }
        })}
      >
        <BestPractices />
      </Box>

      {/* Mobile callout */}
      <Box
        sx={theme => ({
          position: 'absolute',
          top: theme.spacing(3),
          right: theme.spacing(4),
          display: { xs: authSectionActive ? 'none' : 'block', md: 'none' }
        })}
      >
        <IconButton
          color="secondary"
          icon={InfoOutlined}
          onClick={() => setIsOpen(true)}
          aria-label={t('LoggedOutGenerator.CodePreview.calloutIconLabel')}
          data-heap-id="LoggedOutGenerator_CodePreview_BestPracticesButton"
        />
        <Dialog
          hideBackdrop
          open={isOpen}
          onClose={handleClose}
          aria-labelledby={calloutTitleId}
          aria-describedby={calloutDescriptionId}
          fullWidth
        >
          <BestPractices
            titleId={calloutTitleId}
            descriptionId={calloutDescriptionId}
            handleClose={handleClose}
          />
        </Dialog>
      </Box>
    </Stack>
  )
}
